import { useState, useRef } from 'react';
import useWindowWidth from '../hooks/useWindowWidth';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowLeft, Download, Copy } from 'lucide-react';

const FG_PRESETS = [
  { color: '#fb923c', label: 'Orange' },
  { color: '#ffffff', label: 'Weiß' },
  { color: '#38bdf8', label: 'Hellblau' },
  { color: '#0d1b2a', label: 'Navy' },
  { color: '#a78bfa', label: 'Lila' },
  { color: '#4ade80', label: 'Grün' },
  { color: '#f87171', label: 'Rot' },
];

const BG_PRESETS = [
  { color: '#0a0a0a', label: 'Schwarz' },
  { color: '#0d1b2a', label: 'Navy' },
  { color: '#1e293b', label: 'Slate' },
  { color: '#ffffff', label: 'Weiß' },
  { color: '#f1f5f9', label: 'Hell' },
];

function ColorSwatch({ color, selected, onClick, label }) {
  return (
    <button
      onClick={() => onClick(color)}
      title={label}
      style={{
        width: '28px', height: '28px', borderRadius: '6px',
        background: color, border: selected ? '2px solid var(--accent)' : '2px solid transparent',
        outline: selected ? '2px solid var(--accent)' : '2px solid transparent',
        outlineOffset: '2px',
        cursor: 'pointer', flexShrink: 0,
        boxShadow: color === '#ffffff' || color === '#f1f5f9' ? 'inset 0 0 0 1px rgba(0,0,0,0.15)' : 'none',
        transition: 'outline 0.15s',
      }}
    />
  );
}

export default function QRCodeGenerator() {
  const isMobile = useWindowWidth() < 768;
  const [text, setText] = useState('');
  const [size, setSize] = useState(256);
  const [copied, setCopied] = useState(false);
  const [fgColor, setFgColor] = useState('#fb923c');
  const [bgColor, setBgColor] = useState('#0a0a0a');
  const canvasRef = useRef(null);

  const toHex = c => c.replace('#', '');

  const qrUrl = text
    ? `https://api.qrserver.com/v1/create-qr-code/?size=${size}x${size}&data=${encodeURIComponent(text)}&bgcolor=${toHex(bgColor)}&color=${toHex(fgColor)}&format=png`
    : null;

  const handleDownload = async () => {
    if (!qrUrl) return;
    try {
      const response = await fetch(qrUrl);
      const blob = await response.blob();
      const objectUrl = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = objectUrl;
      a.download = 'qrcode.png';
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(objectUrl);
    } catch {
      window.open(qrUrl, '_blank');
    }
  };

  const handleCopyUrl = () => {
    if (navigator.clipboard && window.isSecureContext) {
      navigator.clipboard.writeText(qrUrl);
    } else {
      const ta = document.createElement('textarea');
      ta.value = qrUrl;
      ta.style.position = 'fixed'; ta.style.opacity = '0';
      document.body.appendChild(ta);
      ta.select();
      document.execCommand('copy');
      document.body.removeChild(ta);
    }
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const inputStyle = {
    background: 'var(--bg-secondary)',
    border: '2px solid var(--border)',
    borderRadius: '10px',
    color: 'var(--text-primary)',
    fontFamily: 'inherit',
    fontSize: '16px',
    padding: '14px 18px',
    width: '100%',
  };

  return (
    <div className="section" style={{ minHeight: 'calc(100vh - 140px)' }}>
      <div className="container">
        <Link to="/privat" className="icon-button" style={{ display: 'inline-flex', gap: '8px', alignItems: 'center', marginBottom: '32px' }}>
          <ArrowLeft size={20} />
          <span style={{ fontWeight: 500 }}>Zurück</span>
        </Link>

        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }}>
          <h1 className="section-title">QR-Code Generator</h1>
          <p className="section-subtitle" style={{ marginBottom: '48px' }}>URL, Text oder beliebigen Inhalt als QR-Code</p>

          <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr', gap: '24px', maxWidth: '800px', margin: '0 auto', alignItems: 'start' }}>

            <div style={{ background: 'var(--card-bg)', border: '1px solid var(--border)', borderRadius: '12px', padding: '28px', display: 'flex', flexDirection: 'column', gap: '20px' }}>
              <div>
                <label className="form-label">Inhalt</label>
                <textarea
                  value={text}
                  onChange={e => setText(e.target.value)}
                  placeholder="https://www.youtube.com/watch?v=dQw4w9WgXcQ"
                  style={{ ...inputStyle, minHeight: '120px', resize: 'vertical', lineHeight: 1.5 }}
                />
              </div>

              {/* Farbe QR */}
              <div>
                <label className="form-label">QR-Farbe</label>
                <div style={{ display: 'flex', gap: '8px', alignItems: 'center', flexWrap: 'wrap', marginTop: '6px' }}>
                  {FG_PRESETS.map(p => (
                    <ColorSwatch key={p.color} color={p.color} label={p.label} selected={fgColor === p.color} onClick={setFgColor} />
                  ))}
                  <div style={{ display: 'flex', alignItems: 'center', gap: '6px', marginLeft: '4px' }}>
                    <input
                      type="color"
                      value={fgColor}
                      onChange={e => setFgColor(e.target.value)}
                      title="Eigene Farbe"
                      style={{ width: '28px', height: '28px', borderRadius: '6px', border: '2px solid var(--border)', padding: '0', cursor: 'pointer', background: 'none' }}
                    />
                    <span style={{ fontSize: '11px', color: 'var(--text-secondary)', fontFamily: 'monospace' }}>{fgColor}</span>
                  </div>
                </div>
              </div>

              {/* Hintergrund */}
              <div>
                <label className="form-label">Hintergrund</label>
                <div style={{ display: 'flex', gap: '8px', alignItems: 'center', flexWrap: 'wrap', marginTop: '6px' }}>
                  {BG_PRESETS.map(p => (
                    <ColorSwatch key={p.color} color={p.color} label={p.label} selected={bgColor === p.color} onClick={setBgColor} />
                  ))}
                  <div style={{ display: 'flex', alignItems: 'center', gap: '6px', marginLeft: '4px' }}>
                    <input
                      type="color"
                      value={bgColor}
                      onChange={e => setBgColor(e.target.value)}
                      title="Eigene Farbe"
                      style={{ width: '28px', height: '28px', borderRadius: '6px', border: '2px solid var(--border)', padding: '0', cursor: 'pointer', background: 'none' }}
                    />
                    <span style={{ fontSize: '11px', color: 'var(--text-secondary)', fontFamily: 'monospace' }}>{bgColor}</span>
                  </div>
                </div>
              </div>

              <div>
                <label className="form-label">Größe: {size}×{size}px</label>
                <input
                  type="range"
                  min="128"
                  max="512"
                  step="64"
                  value={size}
                  onChange={e => setSize(Number(e.target.value))}
                  style={{ width: '100%', accentColor: 'var(--accent)', marginTop: '8px' }}
                />
                <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '12px', color: 'var(--text-secondary)', marginTop: '4px' }}>
                  <span>128px</span><span>512px</span>
                </div>
              </div>

              {qrUrl && (
                <div style={{ display: 'flex', gap: '10px' }}>
                  <button onClick={handleDownload} className="btn-primary" style={{ flex: 1, padding: '12px' }}>
                    <Download size={16} /> Herunterladen
                  </button>
                  <button onClick={handleCopyUrl} className="icon-button" style={{ padding: '12px 16px', border: '1px solid var(--border)', borderRadius: '10px' }}>
                    <Copy size={16} />
                    {copied ? 'Kopiert' : 'URL'}
                  </button>
                </div>
              )}
            </div>

            <div style={{ background: 'var(--card-bg)', border: '1px solid var(--border)', borderRadius: '12px', padding: '28px', display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: '300px' }}>
              {qrUrl ? (
                <motion.img
                  key={qrUrl}
                  src={qrUrl}
                  alt="QR Code"
                  initial={{ opacity: 0, scale: 0.9 }}
                  animate={{ opacity: 1, scale: 1 }}
                  transition={{ duration: 0.3 }}
                  style={{ maxWidth: '100%', borderRadius: '8px' }}
                />
              ) : (
                <div style={{ textAlign: 'center', color: 'var(--text-secondary)' }}>
                  <div style={{ fontSize: '48px', marginBottom: '12px', opacity: 0.3 }}>▣</div>
                  <div style={{ fontSize: '14px' }}>Inhalt eingeben</div>
                </div>
              )}
            </div>
          </div>
        </motion.div>
      </div>
    </div>
  );
}
